import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchVapidPublicKey,
  subscribePush,
  unsubscribePush,
  urlBase64ToUint8Array,
} from '~/api/push';

/**
 * Estado do opt-in de Web Push deste dispositivo.
 *
 *  - `unsupported`    : navegador sem Notification ou PushManager (ex: iOS sem PWA instalada)
 *  - `denied`         : usuário negou no nível do navegador; só recupera nas configs do SO
 *  - `granted-active` : permitido, subscription registrada e ID conhecido pelo backend
 *  - `granted-idle`   : permitido mas sem subscription ativa (revogou no SO ou nunca ativou)
 *  - `default`        : nunca pediu — botão "Ativar" disparará o prompt
 */
export type PushSupportState =
  | 'unsupported'
  | 'denied'
  | 'granted-active'
  | 'granted-idle'
  | 'default';

const LOCAL_KEY = 'rx-push-subscription-id';

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

function readStoredId(): string | null {
  try {
    return localStorage.getItem(LOCAL_KEY);
  } catch {
    return null;
  }
}

function persistId(id: string | null): void {
  try {
    if (id) localStorage.setItem(LOCAL_KEY, id);
    else localStorage.removeItem(LOCAL_KEY);
  } catch {
    // localStorage indisponível (modo privado raro) — toleramos perda do ID
  }
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.ready;
}

export interface UsePushSubscriptionValue {
  state: PushSupportState;
  isBusy: boolean;
  errorMessage: string | null;
  enable(): Promise<void>;
  disable(): Promise<void>;
}

export function usePushSubscription(): UsePushSubscriptionValue {
  const [state, setState] = useState<PushSupportState>('default');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isSupported()) {
      setState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    if (Notification.permission !== 'granted') {
      setState('default');
      return;
    }
    const registration = await getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription && readStoredId()) {
      setState('granted-active');
    } else {
      setState('granted-idle');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enableMutation = useMutation({
    mutationFn: async () => {
      if (!isSupported()) throw new Error('Navegador não suporta notificações push.');

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Permissão negada — habilite nas configurações do navegador.');
      }

      const registration = await getRegistration();
      if (!registration) throw new Error('Service worker indisponível.');

      const vapidKey = await fetchVapidPublicKey();

      // Reaproveita subscription existente se a chave bater; senão refaz.
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        }));

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys) {
        throw new Error('Subscription inválida devolvida pelo navegador.');
      }

      const saved = await subscribePush(json);
      persistId(saved.id);
    },
    onSuccess: () => {
      setErrorMessage(null);
      void refresh();
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
      void refresh();
    },
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      const registration = await getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      const id = readStoredId();

      if (subscription) {
        await subscription.unsubscribe();
      }
      if (id) {
        try {
          await unsubscribePush(id);
        } catch {
          // 404 quer dizer que já não existe no backend — ignore.
        }
      }
      persistId(null);
    },
    onSuccess: () => {
      setErrorMessage(null);
      void refresh();
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
      void refresh();
    },
  });

  const enable = useCallback(async () => {
    await enableMutation.mutateAsync();
  }, [enableMutation]);

  const disable = useCallback(async () => {
    await disableMutation.mutateAsync();
  }, [disableMutation]);

  return {
    state,
    isBusy: enableMutation.isPending || disableMutation.isPending,
    errorMessage,
    enable,
    disable,
  };
}

/**
 * Helper para o logout — limpa a subscription deste dispositivo
 * sem disparar refetch/rerender (não usa hooks, pode ser chamado fora de React).
 */
export async function cleanupPushOnLogout(): Promise<void> {
  if (!isSupported()) return;
  try {
    const registration = await getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    const id = readStoredId();
    if (subscription) {
      await subscription.unsubscribe();
    }
    if (id) {
      try {
        await unsubscribePush(id);
      } catch {
        /* tolerante */
      }
    }
  } finally {
    persistId(null);
  }
}
